import { ChevronLeftIcon } from "lucide-react";
import { ChevronRightIcon } from "lucide-react";
import { Button } from "./button";
import {
  useState,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { useToast } from "../../hooks/use-toast";

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

export default function Pagination({
  page,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const { toast } = useToast();
  const [inputValue, setInputValue] = useState(page.toString());

  // Update input value when page prop changes
  useEffect(() => {
    setInputValue(page.toString());
  }, [page]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Allow only numeric input
    const value = e.target.value.replace(/[^0-9]/g, "");
    setInputValue(value);
  };

  const handleInputBlur = () => {
    let newPage = Number.parseInt(inputValue, 10);

    // Handle empty input or invalid values
    if (Number.isNaN(newPage) || inputValue === "") {
      setInputValue(page.toString());
      return;
    }

    // Enforce min/max restrictions
    if (newPage < 1) {
      toast({
        title: "Invalid page number",
        description: "Page number must be at least 1",
      });
      newPage = 1;
    }
    if (newPage > totalPages) {
      toast({
        title: "Invalid page number",
        description: `Page number must be less than ${totalPages}`,
      });
      newPage = totalPages;
    }

    // Update parent component if page changed
    if (newPage !== page) {
      onPageChange(newPage);
    }

    // Ensure input shows the correct value
    setInputValue(newPage.toString());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleInputBlur();
    }
  };

  const safeTotalPages = Number.isNaN(totalPages) || totalPages < 1 ? 1 : totalPages;
  const safePage = Number.isNaN(page) || page < 1 ? 1 : page;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        <span className="mr-2">Page</span>
        <input
          type="text"
          className="w-12 h-8 px-2 text-center border rounded text-black"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          min={1}
          max={safeTotalPages}
        />
        <span className="ml-2 min-w-16">of {safeTotalPages}</span>
      </div>
      <Button
        type="button"
        disabled={safePage <= 1 || safeTotalPages <= 1}
        onClick={() => onPageChange(safePage - 1)}
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        disabled={safePage >= safeTotalPages || safeTotalPages <= 1}
        onClick={() => onPageChange(safePage + 1)}
      >
        <ChevronRightIcon className="w-4 h-4" />
      </Button>
    </div>
  );
}
